import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { Formik, Form } from 'formik';
import { isEmpty } from 'lodash';
import * as yup from 'yup';
import { SchedulerAdditionalFieldsStyles } from './SchedulerAdditionalFieldsStyles';
import Select from '../Inputs/Select';
import TermsOfService from '@components/TermsOfService/TermsOfService'
import Notice from '../Notice/Notice';
import { SiteAPI } from '../../config';
import FInput from '../Inputs/FInput';
import SlideUpComponent from '../SlideUp/SlideUpComponent';
import { UserConsumer } from '../context/User.context';
import localizationData from '../../data/lpLocalizationData';
import timeMap from '@/services/timeMap';

const phoneRegExp = /^(\([0-9]{3}\) |[0-9]{3}-)[0-9]{3}-[0-9]{4}$/;

// Matches entire string
// No string made by only white space
// No string where first character is white space
const businessNameRegExp = /^(?!\s*$|\s).*$/;

const formValidations = {
  first_name: yup
    .string()
    .min(1, 'Must have at least one character')
    .required('Please enter your first name'),
  last_name: yup
    .string()
    .min(1, 'Must have at least one character')
    .max(255, 'Must be shorter than 255 characters')
    .required('Please enter your last name'),
  email: yup
    .string()
    .email('Please enter a valid email address')
    .required('Please enter your email address'),
  phone: yup
    .string()
    .matches(phoneRegExp, 'Please enter a valid phone number')
    .required('Please enter your phone number'),
  business_name: yup
    .string()
    .min(1, 'Must have at least one character')
    .matches(businessNameRegExp, 'Cannot be blank or begin with a space')
    .required('Please enter your business name'),
  industry: yup
    .string()
    .required('Please select your industry')
};

const SchedulerAdditionalFields = ({
  appointment,
  account = null,
  buttonText = 'Confirm My Appointment',
  close,
  reset,
  stage,
  submit,
  title,
  submitError,
  tosText,
  onBack,
  localizationFields = localizationData.en,
  AdditionalQuestions = false,
  hideCloseButton = false,
}) => {
  const [options, setOptions] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [apptMonth, setApptMonth] = useState(null); // numerical (6 for June)
  const [apptDate, setApptDate] = useState(null); // numerical (4)
  const [apptTime, setApptTime] = useState(null); // timeMap[i] -> 3:00pm || 3:30pm
  const [apptTimeZone, setApptTimeZone] = useState(null); // PST || PDT || etc
  let questionValidations = {};

  let initialValues = {
    first_name: account ? account?.first_name : '',
    last_name: account ? account?.last_name : '',
    email: account ? account?.email : '',
    phone: account ? account?.phone : '',
    business_name: account ? account?.business_name : '',
    industry: account ? account?.industry : '',
    sms_opt_in: account ? account?.sms_opt_in : true,
  };

  if (!AdditionalQuestions || typeof (AdditionalQuestions) === 'undefined') {
    AdditionalQuestions = {};
    AdditionalQuestions.expected_annual_revenue = false;
  }

  if (AdditionalQuestions.expected_annual_revenue) {
    initialValues.expected1stYearRevenue = '';
    questionValidations.expected1stYearRevenue = yup.string().required('Please select an option');
  }

  //Checks if campaign 104 exists. If it exists, enter the AB test
  const vwoABNotRequired = (typeof sessionStorage != 'undefined') ? (sessionStorage.getItem('VWO_variation') ? true : false) : false;
  if (vwoABNotRequired) {
    const queryData = JSON.parse(localStorage.getItem('query')) || {};
    queryData.utm_content = 'vwo_v1';
    localStorage.setItem('query', JSON.stringify(queryData));
  }

  const validationSchema = yup.object().shape({
    ...formValidations,
    ...questionValidations,
  });

  const setPicketlistOptions = async () => {
    const { data } = await axios.get(SiteAPI.websiteConfig);
    setOptions(data.picklist_options);
  };

  const isMounted = useRef(false)

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false }
  }, []);

  useEffect(() => {
    setPicketlistOptions();
  }, []);

  useEffect(() => {
    const scheduleFormData = JSON.parse(sessionStorage.getItem('scheduleFormData'));
    const scheduleFormContext = JSON.parse(sessionStorage.getItem('scheduleFormContext'));
    if (scheduleFormData && scheduleFormContext) {
      submitForm(scheduleFormData, scheduleFormContext)
    }
  }, []);

  useEffect(() => {
    if (reset) {
      setIsLoading(false);
    }
  }, [reset]);

  const submitForm = async (data, context) => {
    setIsLoading(true);
    sessionStorage.setItem('scheduleFormData', JSON.stringify(data));
    sessionStorage.setItem('scheduleFormContext', JSON.stringify(context));
    await submit(data, context, true);
    if (isMounted.current) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setPicketlistOptions();
  }, []);

  useEffect(() => {
    //Set the time zone for the appointment
    const formatTimezone = () => dayjs(new Date()).format('z');
    setApptTimeZone(formatTimezone());

    const thisAppt = new Date(0);
    thisAppt.setUTCMilliseconds(appointment.selectedTime);

    const hour = thisAppt.getHours();
    const minutes = thisAppt.getMinutes();
    let appointmentTime;
    if (minutes === 30) {
      appointmentTime = String(timeMap[String(hour) + String(minutes)])
    } else {
      appointmentTime = String(timeMap[String(hour)])
    }

    setApptTime(appointmentTime);
    setApptMonth(thisAppt.getMonth() + 1);
    setApptDate(thisAppt.getDate());
  }, [])

  return (
    <SlideUpComponent hideCloseButton={hideCloseButton} active={stage === 'form'} close={close} onBack={onBack}>
      <SchedulerAdditionalFieldsStyles data-cy="scheduler-additional-fields">
        <div className="container">
          <div className="row">
            <div className="col-12 col-md-12">
              <section className="questions">
                <h3 className={isLoading ? "title hidden" : "title"}>{title}</h3>

                <div className="form-wrap">
                  <UserConsumer>
                    {context => (
                      <Formik
                        enableReinitialize
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={async (values, { setSubmitting }) => {
                          submitForm(values, context);
                          setSubmitting(true);
                        }}
                      >
                        {({
                          values,
                          errors,
                          touched,
                          handleChange,
                          handleBlur,
                          handleSubmit,
                          isSubmitting,
                        }) => (
                          <>

                            <Form
                              onSubmit={handleSubmit}
                              className={isLoading ? 'is-loading' : ''}
                            >
                              <div className='form-background'>
                                {appointment && (
                                  <div className='schedule-time'>{`Scheduled for ${apptMonth}/${apptDate} at ${apptTime} ${apptTimeZone}`}</div>
                                )}
                                <FInput
                                  id="first_name"
                                  label={localizationFields.nameField}
                                  values={values}
                                  touched={touched}
                                  errors={errors}
                                  handleChange={handleChange}
                                  handleBlur={handleBlur}
                                  tests={validationSchema.fields.first_name.tests}
                                />
                                <FInput
                                  id="last_name"
                                  label={localizationFields.lastnameField}
                                  values={values}
                                  touched={touched}
                                  errors={errors}
                                  handleChange={handleChange}
                                  handleBlur={handleBlur}
                                  tests={validationSchema.fields.last_name.tests}
                                />
                                <FInput
                                  id="email"
                                  label={localizationFields.emailField}
                                  type="email"
                                  values={values}
                                  touched={touched}
                                  errors={errors}
                                  handleChange={handleChange}
                                  handleBlur={handleBlur}
                                  tests={validationSchema.fields.email.tests}
                                  info="We’ll send the appointment confirmation to your inbox."
                                />
                                <FInput
                                  id="phone"
                                  label={localizationFields.phoneField}
                                  values={values}
                                  touched={touched}
                                  errors={errors}
                                  handleChange={handleChange}
                                  handleBlur={handleBlur}
                                  type="tel"
                                  tests={validationSchema.fields.phone.tests}
                                  info="A small business specialist will call you at the date and time you selected."
                                />
                                <FInput
                                  id="business_name"
                                  label={localizationFields.businessNameField}
                                  values={values}
                                  touched={touched}
                                  errors={errors}
                                  handleChange={handleChange}
                                  handleBlur={handleBlur}
                                  tests={validationSchema.fields.business_name.tests}
                                  info="Input the name of your business. If you don’t have a business, please put ‘none’."
                                />
                                <Select
                                  id="industry"
                                  label={localizationFields.industry}
                                  touched={touched}
                                  errors={errors}
                                  handleChange={handleChange}
                                  handleBlur={handleBlur}
                                  values={values}
                                  options={options.industry}
                                  addNoneOption={true}
                                  tests={validationSchema.fields.industry.tests}
                                  info="Select the industry that best describes your business. If you don't see an industry that matches your business, select 'other'."
                                />
                                {
                                  AdditionalQuestions.expected_annual_revenue && (
                                    <Select
                                      id="expected1stYearRevenue"
                                      label={localizationFields.expected1stYearRevenue}
                                      touched={touched}
                                      errors={errors}
                                      handleChange={handleChange}
                                      handleBlur={handleBlur}
                                      tests={validationSchema.fields.expected1stYearRevenue.tests}
                                      values={values}
                                      options={options.expected_1st_year_revenue}
                                      info="This information helps us match you with the best accountant
                                    for your situation. If you’re not in business yet, please select $0. "
                                    />
                                  )
                                }
                                {submitError ? (
                                  <Notice type="error" message={submitError} />
                                ) : null}
                              </div>
                              <div>
                                <button
                                  type="submit"
                                  label=""
                                  className="btn btn-submit btn-secondary btn-block"
                                  disabled={isSubmitting || !isEmpty(errors)}
                                >
                                  {buttonText}
                                </button>
                              </div>
                              <div className="terms-text">
                                <TermsOfService
                                  id="sms_opt_in"
                                  buttonText={buttonText}
                                />
                              </div>

                            </Form>


                            {isLoading && (
                              <div className="loading-message">
                                <h4>Just a moment…</h4>
                                <p>
                                  We're scheduling your appointment...
                                  <br />
                                  <strong>Please keep this window open.</strong>
                                </p>
                              </div>
                            )}
                          </>
                        )}
                      </Formik>
                    )}
                  </UserConsumer>
                </div>


              </section>
            </div>

          </div>
        </div>
      </SchedulerAdditionalFieldsStyles>
    </SlideUpComponent>
  );
};

export default SchedulerAdditionalFields;
